import { Divider, Input, Spin } from 'antd';
import { ORGANISATIONS_PAGE_PATH } from 'constants/routeConstants';
import useFetchOrganisations from 'hooks/useFetchOrganisations';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import useOpenOrganisation from 'hooks/useOpenOrganisation';
import React, { useCallback, useMemo, useState } from 'react';
import { BsCheckLg } from 'react-icons/bs';
import { IoArrowBack } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/Store';

interface OrganisationSelectorProps {
  onSelect?: () => void;
}

const OrganisationSelector: React.FC<OrganisationSelectorProps> = ({
  onSelect,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { organisationId: activeOrganisationId, isSwitchingOrganisation } =
    useSelector((state: RootState) => state.activeOrganisationReducer);

  const {
    data: organisations = [],
    isLoading: isLoadingOrganisations,
    isError: isErrorOrganisations,
  } = useFetchOrganisations();

  const { openOrganisation } = useOpenOrganisation();
  const { isLoading: isLoadingUserData } = useFetchUserProfile();

  const isDisabled =
    isLoadingOrganisations || isSwitchingOrganisation || isLoadingUserData;

  const filteredOrgs = useMemo(() => {
    const sorted = [...organisations].sort((a, b) =>
      (a.orgName ?? '').localeCompare(b.orgName ?? '', undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    );
    if (!searchTerm.trim()) return sorted;
    const lower = searchTerm.toLowerCase();
    return sorted.filter((org) =>
      (org.orgName ?? '').toLowerCase().includes(lower)
    );
  }, [organisations, searchTerm]);

  const handleSelect = useCallback(
    (orgId: string) => {
      if (isDisabled) return;
      if (orgId !== activeOrganisationId) {
        openOrganisation(orgId);
      }
      onSelect?.();
    },
    [activeOrganisationId, openOrganisation, onSelect, isDisabled]
  );

  if (isLoadingOrganisations || isLoadingUserData) {
    return (
      <div className="flex flex-col items-center py-6 text-sm text-gray-muted">
        <Spin />
        <span className="mt-2">Loading organisations...</span>
      </div>
    );
  }

  if (isErrorOrganisations) {
    return (
      <div className="px-2 py-3 text-sm text-danger">
        Failed to load organisations. Please try again.
      </div>
    );
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        className="flex cursor-pointer items-center whitespace-nowrap rounded-md px-3 py-2 text-base font-medium hover:bg-gray-100"
        onClick={() => {
          if (!isDisabled) {
            navigate(ORGANISATIONS_PAGE_PATH);
            onSelect?.();
          }
        }}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
            e.preventDefault();
            navigate(ORGANISATIONS_PAGE_PATH);
            onSelect?.();
          }
        }}
      >
        <IoArrowBack className="mr-2 flex-shrink-0" size={14} />
        Return to Organisation list
      </div>

      <Divider className="mb-2 mt-0" />

      <Input
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
        autoFocus
        className="mb-2"
      />

      <div className="max-h-80 overflow-y-auto">
        {filteredOrgs.length === 0 && (
          <div className="px-3 py-3 text-sm text-gray-muted">
            No organisations found
          </div>
        )}
        {filteredOrgs.map((org) => {
          const isActive = org.id === activeOrganisationId;
          return (
            <div
              key={org.id}
              role="button"
              tabIndex={0}
              className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm ${
                isActive
                  ? 'bg-success-light font-semibold text-charcoal'
                  : 'text-gray-dark hover:bg-gray-100'
              }`}
              onClick={() => handleSelect(org.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(org.id);
                }
              }}
            >
              <span className="truncate">{org.orgName}</span>
              {isActive && (
                <BsCheckLg
                  className="ml-2 flex-shrink-0 text-success"
                  size={18}
                />
              )}
            </div>
          );
        })}
      </div>

      {isSwitchingOrganisation && (
        <div className="flex items-center justify-center py-2 text-sm text-gray-muted">
          <Spin size="small" className="mr-2" />
          Opening organisation...
        </div>
      )}
    </div>
  );
};

export default OrganisationSelector;
